import { useBinnedDataBufferCache } from 'features/dataLayers/load/useBinnedDataBufferCache'
import { RolodexCacheInstance } from 'features/rolodex'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { createCdf, CreateCdfParams } from './cdfGenerator'

const EMPTY_CREATE_RESULT = {
  entry: null,
  getBinnedDataBufferFn: null,
}

export function useCreateCustomCdf(
  args: CreateCdfParams | null,
  rolodexCacheInstance: RolodexCacheInstance,
  customId: string | null = null
) {
  // TODO: This happens on the render loop - probably wanna put it in a worker if it's slow
  const reactId = useId()

  // A number that keeps track of if the generation has changed
  const generationNumber = useRef(args?.generation ?? 0)
  const { entry, getBinnedDataBufferFn } = useMemo(() => {
    if (!args) {
      return EMPTY_CREATE_RESULT
    }
    if (!args.generation) {
      generationNumber.current = generationNumber.current + 1
    }
    const id = customId ?? `custom-cdf-${reactId}`
    return (
      createCdf(id, {
        generation: generationNumber.current,
        ...args,
      }) ?? EMPTY_CREATE_RESULT
    )
  }, [reactId, args, customId])

  const customCdfId = useMemo(() => {
    return entry?.id ?? null
  }, [entry])

  const { putEntry, resetEntry } = rolodexCacheInstance
  const { putBinnedDataBufferInCache, deleteCachedBinnedDataBuffer } =
    useBinnedDataBufferCache()

  const clearCachedCdf = useCallback(
    (cdfId: string) => {
      resetEntry(cdfId)
      deleteCachedBinnedDataBuffer(cdfId)
    },
    [deleteCachedBinnedDataBuffer, resetEntry]
  )

  const insertCachedCdf = useCallback(() => {
    if (!entry || !getBinnedDataBufferFn) {
      return
    }
    putEntry(entry)
    putBinnedDataBufferInCache({
      id: entry.id,
      generation: entry.generation,
      data: getBinnedDataBufferFn,
    })
  }, [entry, getBinnedDataBufferFn, putEntry, putBinnedDataBufferInCache])

  const [insertedId, setInsertedId] = useState<string | null>(null)

  useEffect(() => {
    if (insertedId && insertedId !== customCdfId) {
      clearCachedCdf(insertedId)
    }
    if (customCdfId) {
      insertCachedCdf()
      setInsertedId(customCdfId)
    }
  }, [clearCachedCdf, customCdfId, insertCachedCdf, insertedId])

  return { id: customCdfId, isLoading: !!args && !customCdfId }
}
